import { resolveRolePermissions } from './defaultRoles';
import type { ModuleId, PermissionId, Role } from '../types';
import { MODULE_IDS } from './selectThemes';

export type { PermissionId };

export interface ModuleSubPermission {
  id: string;
  label: string;
}

/** Granular capabilities grouped under each app module (Admin → Roles). */
export const MODULE_SUB_PERMISSIONS: Partial<Record<ModuleId, ModuleSubPermission[]>> = {
  orders: [
    { id: 'view', label: 'View orders list & detail' },
    { id: 'pricing', label: 'View order pricing & payments' },
    { id: 'create', label: 'Create new orders' },
    { id: 'edit', label: 'Edit order details' },
  ],
  accounts: [
    { id: 'view', label: 'View accounts queue' },
    { id: 'manage', label: 'Invoices & payments' },
  ],
  design: [
    { id: 'workflow', label: 'Design workflow board' },
    { id: 'settings', label: 'Department settings' },
    { id: 'assign', label: 'Assign & reassign work' },
  ],
  production: [
    { id: 'workflow', label: 'Production workflow board' },
    { id: 'settings', label: 'Department settings' },
    { id: 'assign', label: 'Assign & reassign work' },
  ],
  finishing: [
    { id: 'workflow', label: 'Finishing workflow board' },
    { id: 'settings', label: 'Department settings' },
    { id: 'assign', label: 'Assign & reassign work' },
  ],
  qc: [
    { id: 'workflow', label: 'QC workflow board' },
    { id: 'settings', label: 'Department settings' },
    { id: 'assign', label: 'Assign & reassign work' },
  ],
  dispatch: [
    { id: 'workflow', label: 'Dispatch & delivery board' },
    { id: 'assign', label: 'Assign & reassign work' },
  ],
  installation: [
    { id: 'workflow', label: 'Installation workflow board' },
    { id: 'settings', label: 'Department settings' },
    { id: 'assign', label: 'Assign & reassign work' },
  ],
  reports: [{ id: 'view', label: 'View reports' }],
  settings: [{ id: 'manage', label: 'Application settings' }],
  admin: [
    { id: 'users', label: 'Manage users' },
    { id: 'roles', label: 'Manage roles' },
    { id: 'catalog', label: 'Products, materials & clients' },
    { id: 'audit', label: 'Audit log' },
  ],
};

const DEFAULT_SUB: ModuleSubPermission[] = [{ id: 'access', label: 'Module access' }];

export function getModuleSubs(module: ModuleId): ModuleSubPermission[] {
  return MODULE_SUB_PERMISSIONS[module] ?? DEFAULT_SUB;
}

export function subPermissionKey(module: ModuleId, subId: string): PermissionId {
  return `${module}.${subId}`;
}

export function getSelectedSubIds(permissions: readonly PermissionId[], module: ModuleId): string[] {
  const subs = getModuleSubs(module);
  if (permissions.includes(module)) return subs.map((s) => s.id);
  return subs
    .filter((s) => permissions.includes(subPermissionKey(module, s.id)))
    .map((s) => s.id);
}

export function isModuleFullyGranted(permissions: readonly PermissionId[], module: ModuleId): boolean {
  const subs = getModuleSubs(module);
  return getSelectedSubIds(permissions, module).length === subs.length;
}

export function isModulePartiallyGranted(permissions: readonly PermissionId[], module: ModuleId): boolean {
  const count = getSelectedSubIds(permissions, module).length;
  return count > 0 && count < getModuleSubs(module).length;
}

export function roleCanUseModule(roleId: string, module: ModuleId, rolesList: Role[]): boolean {
  if (roleId === 'admin') return true;
  return roleHasModule(resolveRolePermissions(roleId, rolesList), module);
}

/** True if the role has the whole module or any granular sub-permission under it. */
export function roleHasModule(permissions: readonly PermissionId[], module: ModuleId): boolean {
  if (permissions.includes('admin')) return true;
  if (permissions.includes(module)) return true;
  return permissions.some((p) => typeof p === 'string' && p.startsWith(`${module}.`));
}

export function roleHasSubPermission(
  permissions: readonly PermissionId[],
  module: ModuleId,
  subId: string,
): boolean {
  if (permissions.includes('admin')) return true;
  if (permissions.includes(module)) return true;
  return permissions.includes(subPermissionKey(module, subId));
}

export function countGrantedModules(permissions: readonly PermissionId[]): number {
  return MODULE_IDS.filter((m) => m !== 'admin' && roleHasModule(permissions, m)).length;
}

export function setModuleSubSelection(
  permissions: PermissionId[],
  module: ModuleId,
  selectedSubIds: string[],
): PermissionId[] {
  const without = permissions.filter(
    (p) => p !== module && !(typeof p === 'string' && p.startsWith(`${module}.`)),
  );
  const subs = getModuleSubs(module);
  if (selectedSubIds.length === 0) return without;
  if (selectedSubIds.length === subs.length) return [...without, module];
  return [
    ...without,
    ...selectedSubIds.map((id) => subPermissionKey(module, id)),
  ];
}

/** Modules shown in the role editor (admin is implicit for the admin role). */
export const ROLE_EDITOR_MODULE_IDS = MODULE_IDS.filter((m) => m !== 'admin');
